import { DEFAULT_DAILY_SEGMENT_LIMIT, DEFAULT_SPEND_LIMIT_CENTS } from './plan.js';

/**
 * `workspaces.settings.campaigns` (M9, owner decisions 2026-10-14 #18, #35), edited by admins in
 * Settings → Campaigns. Stored in snake_case (the settings backstop trigger reads it too).
 *  - spend_limit_cents: the most one campaign's estimate may cost (whole cents).
 *  - daily_segment_limit: segments the workspace may hand over per Pacific day (the brand cap).
 * The consent sources that count for campaigns are NOT here: they are a column admins cannot write
 * (`workspaces.campaign_consent_sources`), changed only after counsel's review.
 */

export const SPEND_LIMIT_MIN_CENTS = 100;
export const SPEND_LIMIT_MAX_CENTS = 10_000_000;
export const DAILY_SEGMENT_LIMIT_MAX = 1_000_000;

export interface CampaignSettings {
  /** Cents, as a decimal string (money.ts). */
  spendLimitCents: string;
  dailySegmentLimit: number;
}

function wholeIn(value: unknown, min: number, max: number): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max
    ? value
    : null;
}

/** Reads whatever is stored; anything missing or malformed becomes its default. */
export function readCampaignSettings(settings: unknown): CampaignSettings {
  const root =
    settings && typeof settings === 'object' ? (settings as Record<string, unknown>) : {};
  const c =
    root.campaigns && typeof root.campaigns === 'object'
      ? (root.campaigns as Record<string, unknown>)
      : {};
  const spend = wholeIn(c.spend_limit_cents, SPEND_LIMIT_MIN_CENTS, SPEND_LIMIT_MAX_CENTS);
  return {
    spendLimitCents: spend === null ? DEFAULT_SPEND_LIMIT_CENTS : String(spend),
    dailySegmentLimit:
      wholeIn(c.daily_segment_limit, 1, DAILY_SEGMENT_LIMIT_MAX) ?? DEFAULT_DAILY_SEGMENT_LIMIT,
  };
}

export interface CampaignSettingsForm {
  /** Dollars as typed ("100" or "250.50"). */
  spendLimit: string;
  dailySegmentLimit: string;
}

export function toCampaignSettingsForm(s: CampaignSettings): CampaignSettingsForm {
  const cents = Number(s.spendLimitCents);
  return {
    spendLimit: cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2),
    dailySegmentLimit: String(s.dailySegmentLimit),
  };
}

export type CampaignSettingsIssues = Partial<Record<keyof CampaignSettingsForm, string>>;

const DOLLARS = /^\$?\s*(\d{1,6})(?:\.(\d{1,2}))?$/;

function dollarsToWholeCents(value: string): number | null {
  const match = DOLLARS.exec(value.trim().replaceAll(',', ''));
  if (!match) return null;
  return Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
}

export function checkCampaignSettingsForm(form: CampaignSettingsForm): CampaignSettingsIssues {
  const issues: CampaignSettingsIssues = {};
  const cents = dollarsToWholeCents(form.spendLimit);
  if (cents === null) issues.spendLimit = 'Enter an amount in dollars, like 100';
  else if (cents < SPEND_LIMIT_MIN_CENTS || cents > SPEND_LIMIT_MAX_CENTS) {
    issues.spendLimit = 'Use an amount from $1 to $100,000';
  }
  const limit = /^\d+$/.test(form.dailySegmentLimit.trim().replaceAll(',', ''))
    ? Number(form.dailySegmentLimit.trim().replaceAll(',', ''))
    : null;
  if (limit === null || limit < 1 || limit > DAILY_SEGMENT_LIMIT_MAX) {
    issues.dailySegmentLimit = 'Enter a whole number from 1 to 1,000,000';
  }
  return issues;
}

/** `settings.campaigns` as stored. A type alias, so it fits a JSON column type. */
export type StoredCampaignSettings = {
  spend_limit_cents: number;
  daily_segment_limit: number;
};

/** Call after checkCampaignSettingsForm passed. */
export function toStoredCampaignSettings(form: CampaignSettingsForm): StoredCampaignSettings {
  return {
    spend_limit_cents: dollarsToWholeCents(form.spendLimit) ?? Number(DEFAULT_SPEND_LIMIT_CENTS),
    daily_segment_limit: Number(form.dailySegmentLimit.trim().replaceAll(',', '')),
  };
}
